import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  History,
  AlertCircle,
  TrendingDown,
  Filter,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { trpcClient } from "@/lib/trpc";
import { formatNumber, formatMoney, cn, viIncludes } from "@/lib/utils";
import { ListPageShell, useServerListFilter } from "@/components/shared/ListPageShell";
import { useExcelExport } from "@/hooks/useExcelExport";
import type { ExcelColumnDef } from "@/lib/excel.utils";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import {
  InlineFilterRow,
  type InlineFilterDefinition,
} from "@/components/shared/InlineFilterRow";
import { ResponsiveDataView } from "@/components/shared/ResponsiveDataView";
import { toast } from "sonner";
import { endOfMonthDateString, toVnMonthString } from "@apv-erp/status";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

// ── Constants ──────────────────────────────────────────────────────────────────
const ITEMS_PER_PAGE = 50;

const FILTER_FIELDS: InlineFilterDefinition[] = [
  { key: "vendor", placeholder: "Tên / mã NCC...", type: "text" },
  { key: "initialDebt", type: "none" },
  { key: "orderValue", type: "none" },
  { key: "collected", type: "none" },
  { key: "currentDebt", type: "none" },
  { key: "creditLimit", type: "none" },
  {
    key: "status",
    placeholder: "Tất cả",
    type: "select",
    options: [
      { value: "has-debt", label: "Đang nợ" },
      { value: "good", label: "Tốt (Không nợ)" },
    ],
  },
];

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "code", label: "Mã NCC", width: 14 },
  { key: "name", label: "Nhà cung cấp", width: 30 },
  { key: "shortName", label: "Tên ngắn", width: 18 },
  { key: "phone", label: "Số điện thoại", width: 16 },
  { key: "_initialDebt", label: "Nợ đầu kỳ (VNĐ)", width: 18, format: "currency" },
  { key: "_orderValue", label: "Phát sinh trong kỳ (VNĐ)", width: 20, format: "currency" },
  { key: "_collected", label: "Đã thanh toán (VNĐ)", width: 18, format: "currency" },
  { key: "_currentDebt", label: "Nợ còn lại (VNĐ)", width: 18, format: "currency" },
];

// ── Helpers ────────────────────────────────────────────────────────────────────
const daysSince = (d: string | Date | null | undefined): number => {
  if (!d) return Infinity;
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
};

export default function VendorDebtList() {
  const navigate = useNavigate();
  const [debtFilter, setDebtFilter] = useState<
    "all" | "has-debt" | "over-limit"
  >("all");
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const [monthFilter, setMonthFilter] = useState(() => toVnMonthString());

  // Tháng chọn → ngày đầu/cuối tháng 'YYYY-MM-DD'; máy chủ hiểu là trọn ngày theo giờ Việt Nam
  // Ô tháng bị xoá trắng → giữ tháng hiện tại (trước đây ra "-01" làm sập trang — FB-69PVM4, FB-WVM2S3)
  const periodMonth = /^\d{4}-\d{2}$/.test(monthFilter) ? monthFilter : toVnMonthString();
  const startDate = `${periodMonth}-01`;
  const endDate = endOfMonthDateString(startDate);

  // ── Server-side Data ──
  const {
    searchQuery,
    paginatedItems,
    isLoading,
    currentPage,
    totalItems,
    onPageChange,
  } = useServerListFilter<any>({
    queryKey: "vendor-debts",
    queryFn: (params) =>
      trpcClient.vendors.list.query({ ...params, limit: 1000 }) as any,
    extraParams: { startDate, endDate, includeDebt: true },
    defaultLimit: 1000, // Load more for client-side sorting/filtering of debts
  });

  // Số công nợ do máy chủ tính từ sổ công nợ NCC (trước: tự cộng ở trình duyệt từ cột không ai ghi → hiện 0)
  const enrichedItems = useMemo(() => {
    return (paginatedItems || []).map((c: any) => {
      const initialDebt = Number(c.debtOpening) || 0;
      const orderValue = Number(c.debtIncurred) || 0;
      const collected = Number(c.debtPaid) || 0;
      const currentDebt = Number(c.debtClosing) || 0;
      const isOverLimit = false; // No credit limit for vendors

      return {
        ...c,
        _initialDebt: initialDebt,
        _orderValue: orderValue,
        _collected: collected,
        _currentDebt: currentDebt,
        _isOverLimit: isOverLimit,
        _lastOrderDate: c.outsourcePOs?.[0]?.createdAt || null,
      };
    });
  }, [paginatedItems]);

  const displayItems = useMemo(() => {
    let items = enrichedItems;

    // Header search
    if ((searchQuery || "").trim()) {
      items = items.filter(
        (item) =>
          viIncludes(item.code, searchQuery) ||
          viIncludes(item.name, searchQuery) ||
          viIncludes(item.shortName, searchQuery),
      );
    }

    // Debt status filter
    if (debtFilter === "has-debt") {
      items = items.filter((c) => c._currentDebt > 0);
    } else if (debtFilter === "over-limit") {
      items = items.filter((c) => c._isOverLimit);
    }

    // Inline filters
    if (filterValues.vendor) {
      const term = filterValues.vendor.toLowerCase();
      items = items.filter(
        (c) =>
          c.code?.toLowerCase().includes(term) ||
          c.name?.toLowerCase().includes(term) ||
          c.shortName?.toLowerCase().includes(term),
      );
    }
    if (filterValues.status === "has-debt") {
      items = items.filter((c) => c._currentDebt > 0);
    } else if (filterValues.status === "good") {
      items = items.filter((c) => c._currentDebt <= 0);
    }

    // Sort by debt descending
    return [...items].sort((a, b) => b._currentDebt - a._currentDebt);
  }, [enrichedItems, searchQuery, debtFilter, filterValues]);

  // ── Stats ──
  const totalDebt = useMemo(
    () => displayItems.reduce((sum, item) => sum + item._currentDebt, 0),
    [displayItems],
  );
  const overLimitCount = useMemo(
    () => displayItems.filter((item) => item._isOverLimit).length,
    [displayItems],
  );

  // ── Pagination ──
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(50);
  const pagedItems = displayItems.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const rowIds = useMemo(() => pagedItems.map((v: any) => v.id), [pagedItems]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;

  const { handleExport, isExporting } = useExcelExport({
    columns: EXPORT_COLUMNS,
    filename: `cong_no_ncc_${monthFilter}`,
    localData: displayItems,
    selectedIds,
  });

  // Bấm dòng: mở sổ công nợ của nhà cung cấp (route nhận id NCC)
  const openLedger = (vendorId: string) =>
    navigate({ to: "/finance/ap/ledger/$id" as any, params: { id: vendorId } as any });

  return (
    <>
    <ListPageShell
      unitLabel="nhà cung cấp"
      totalItems={displayItems.length}
      currentPage={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      showFilter={showFilter}
      onToggleFilter={() => setShowFilter((prev) => !prev)}
      onClearAll={() => {
        setFilterValues({});
        setDebtFilter("all");
        setPage(1);
      }}
      filterFields={FILTER_FIELDS as any}
      filterValues={filterValues}
      isLoading={isLoading}
      dateFilter={
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1">
          <span className="text-[11px] font-semibold text-slate-500 uppercase">
            Kỳ đối soát:
          </span>
          <input
            type="month"
            value={monthFilter}
            onChange={(e) => e.target.value && setMonthFilter(e.target.value)}
            className="text-xs bg-transparent border-none outline-none font-medium text-indigo-700 w-[110px]"
          />
        </div>
      }
      headerFilters={
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={debtFilter === "all" ? "default" : "outline"}
            size="sm"
            className="h-7 text-[11px] rounded-full"
            onClick={() => { setDebtFilter("all"); setPage(1); }}
          >
            Tất cả ({displayItems.length})
          </Button>
          <Button
            variant={debtFilter === "has-debt" ? "default" : "outline"}
            size="sm"
            className={cn(
              "h-7 text-[11px] rounded-full",
              debtFilter === "has-debt" ? "bg-rose-600" : "",
            )}
            onClick={() => { setDebtFilter("has-debt"); setPage(1); }}
          >
            Đang nợ
          </Button>
        </div>
      }
      actions={
        <div className="flex items-center gap-1.5">
          <ExportImportButtons
            onExport={() => handleExport()}
            exportLoading={isExporting}
            exportOnly
          />
        </div>
      }
    >
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden flex flex-col">
        <ResponsiveDataView
          items={pagedItems}
            emptyState="Không có dữ liệu công nợ"
            renderMobileCard={(item) => (
              <div
                className="flex flex-col bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden active:scale-[0.99] transition-transform"
                onClick={() => openLedger(item.id)}
              >
                <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-bold text-slate-900 line-clamp-1 text-sm">
                      {item.shortName || item.name}
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">
                      {item.code}
                    </span>
                  </div>
                  <div>
                    {item._isOverLimit ? (
                      <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px] gap-1 shrink-0 px-2 py-0.5 h-5 min-h-0 border-none">
                        <AlertCircle size={10} /> Vượt HM
                      </Badge>
                    ) : item._currentDebt > 0 ? (
                      <Badge
                        variant="outline"
                        className="text-amber-600 border-amber-200 bg-amber-50 text-[10px] shrink-0 px-2 py-0.5 h-5 min-h-0 border-none"
                      >
                        Đang nợ
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-emerald-600 border-emerald-200 bg-emerald-50 text-[10px] shrink-0 px-2 py-0.5 h-5 min-h-0 border-none"
                      >
                        Tốt
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="p-3 grid grid-cols-2 gap-3">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-500">
                      Nợ đầu kỳ
                    </span>
                    <span className="text-sm font-semibold text-slate-700 tabular-nums">
                      {formatMoney(item._initialDebt)}
                    </span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[10px] text-slate-500">
                      Phát sinh
                    </span>
                    <span className="text-sm font-semibold text-indigo-600 tabular-nums">
                      +{formatMoney(item._orderValue)}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-500">Đã chi</span>
                    <span className="text-sm font-semibold text-emerald-600 tabular-nums">
                      {formatMoney(item._collected)}
                    </span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[10px] text-slate-500 font-bold text-rose-600">
                      Nợ hiện tại
                    </span>
                    <span className="text-sm font-bold text-rose-600 tabular-nums">
                      {formatMoney(item._currentDebt)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                  <th className="text-left px-4 py-3 font-bold text-slate-600 uppercase tracking-wider text-[10px] w-[250px]">
                    Nhà cung cấp
                  </th>
                  <th className="text-right px-4 py-3 font-bold text-slate-600 uppercase tracking-wider text-[10px]">
                    Nợ đầu kỳ
                  </th>
                  <th className="text-right px-4 py-3 font-bold text-slate-600 uppercase tracking-wider text-[10px]">
                    Phát sinh (+/–)
                  </th>
                  <th className="text-right px-4 py-3 font-bold text-slate-600 uppercase tracking-wider text-[10px]">
                    Đã thanh toán
                  </th>
                  <th className="text-right px-4 py-3 font-bold text-rose-600 uppercase tracking-wider text-[10px] bg-rose-50/30">
                    Nợ hiện tại
                  </th>
                  <th className="text-right px-4 py-3 font-bold text-slate-600 uppercase tracking-wider text-[10px]">
                    Hạn mức
                  </th>
                  <th className="text-center px-4 py-3 font-bold text-slate-600 uppercase tracking-wider text-[10px] w-[140px]">
                    Trạng thái
                  </th>
                </tr>
                {showFilter && (
                  <InlineFilterRow
                    columns={[
                      null,
                      { key: 'vendor' },
                      null,
                      null,
                      null,
                      null,
                      null,
                      { key: 'status' },
                    ]}
                    fields={FILTER_FIELDS}
                    values={filterValues}
                    onChange={handleFilterChange}
                  />
                )}
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="px-2 py-4" />
                      <td className="px-4 py-4">
                        <Skeleton className="h-4 w-32" />
                      </td>
                      <td className="px-4 py-4">
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </td>
                      <td className="px-4 py-4">
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </td>
                      <td className="px-4 py-4">
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </td>
                      <td className="px-4 py-4 bg-rose-50/10">
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </td>
                      <td className="px-4 py-4">
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </td>
                      <td className="px-4 py-4">
                        <Skeleton className="h-4 w-24 mx-auto" />
                      </td>
                    </tr>
                  ))
                ) : pagedItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-20 text-slate-400"
                    >
                      Không có dữ liệu công nợ
                    </td>
                  </tr>
                ) : (
                  pagedItems.map((item) => (
                    <DataTableRow
                      key={item.id}
                      id={item.id}
                      rowSelection={rowSelection}
                      className="border-b border-slate-50 hover:bg-slate-50/80 transition-colors group"
                      onClick={() => openLedger(item.id)}
                    >
                      <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} />
                      <td className="px-4 py-3">
                        <span className="font-semibold text-slate-900 mr-1.5">{item.code}</span>
                        <span className="text-slate-700">{item.shortName || item.name}</span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                        {formatMoney(item._initialDebt)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-indigo-600 font-medium">
                        +{formatMoney(item._orderValue)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-600">
                        {formatMoney(item._collected)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-bold text-rose-600 bg-rose-50/20">
                        {formatMoney(item._currentDebt)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                        {item._creditLimit > 0
                          ? formatMoney(item._creditLimit)
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center">
                          {item._isOverLimit ? (
                            <Badge className="bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100 text-[10px] gap-1 shrink-0">
                              <AlertCircle size={10} /> Vượt hạn mức
                            </Badge>
                          ) : item._currentDebt > 0 ? (
                            <Badge
                              variant="outline"
                              className="text-amber-600 border-amber-200 bg-amber-50 text-[10px] shrink-0"
                            >
                              Đang nợ
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="text-emerald-600 border-emerald-200 bg-emerald-50 text-[10px] shrink-0"
                            >
                              Tốt
                            </Badge>
                          )}
                        </div>
                      </td>
                    </DataTableRow>
                  ))
                )}
              </tbody>
            </table>
          </ResponsiveDataView>
        </div>
      </ListPageShell>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={[
          {
            icon: <Download className="h-4 w-4" />,
            label: `Xuất Excel ${selectedCount} dòng`,
            onClick: () => handleExport(),
          },
        ]}
      />
    </>
  );
}
